import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { initializeAuthFlow, checkAuthStatus } from '../utils/auth';
import { loadConfig } from '../utils/config';

const features = [
  {
    title: 'Customizable Links',
    description: 'Create beautiful, branded links with custom icons, badges, and positioning on your stream',
    icon: '🎨'
  },
  {
    title: 'Flexible Positioning',
    description: 'Place your links anywhere on screen - top, bottom, left, right, or center to match your stream layout',
    icon: '📍'
  },
  {
    title: 'Theme Support',
    description: 'Match your stream theme with light and dark modes, custom sizes, and beautiful icons',
    icon: '✨'
  }
];

const LandingPage = () => {
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const config = await loadConfig();
        if (!config) {
          throw new Error('Failed to load configuration');
        }
        setConfig(config);

        const { isAuthenticated, userData, error } = await checkAuthStatus(config);
        
        if (isAuthenticated && userData) {
          // User is already authenticated, redirect to dashboard
          navigate('/dashboard');
          return;
        }

        if (error && error !== 'No session found') {
          console.error('Auth check error:', error);
        }
      } catch (error) {
        console.error('Failed to check auth status:', error);
        setError('Failed to load application configuration');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const handleTwitchConnect = async () => {
    if (!config) {
      setError('Configuration not loaded');
      return;
    }

    try {
      // Generate and store PKCE challenge and state
      const { codeChallenge, state } = await initializeAuthFlow();
      
      // Redirect to Twitch auth with code challenge and state
      const params = new URLSearchParams({
        client_id: 'v4dopyoiq5vwj8vhavfuqv164f6rky',
        redirect_uri: config.api.twitch.redirectUri,
        response_type: 'code',
        scope: 'channel:read:subscriptions',
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state: state
      });

      window.location.href = `https://id.twitch.tv/oauth2/authorize?${params.toString()}`;
    } catch (error) {
      console.error('Failed to initialize auth flow:', error);
      setError('Failed to initialize authentication');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      {/* Hero Section */}
      <div className="hero min-h-[70vh] bg-base-100">
        <div className="hero-content text-center">
          <div className="max-w-md">
            <h1 className="text-5xl font-bold">XStreamLink</h1>
            <p className="py-6">Enhance your Twitch stream with beautiful, customizable link displays. Create an interactive hub for your game, merchandise, and social links!</p>
            <button 
              className="btn btn-primary"
              onClick={handleTwitchConnect}
              disabled={!config}
            >
              {config ? 'Connect With Twitch' : 'Loading...'}
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 px-4">
        <h2 className="text-3xl font-bold text-center mb-12">Powerful Link Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <div key={index} className="card bg-base-100 shadow-xl">
              <div className="card-body items-center text-center">
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="card-title">{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* How It Works Section */}
      <div className="bg-base-100 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="steps steps-vertical lg:steps-horizontal">
            <div className="step step-primary">Configure your links</div>
            <div className="step step-primary">Customize appearance</div>
            <div className="step step-primary">Add to Twitch</div>
            <div className="step step-primary">Go live!</div>
          </div>
        </div>
      </div>

      {/* Example Section */}
      <div className="py-20 px-4 bg-base-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Link Customization</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card bg-base-200">
              <div className="card-body">
                <h3 className="card-title justify-center mb-4">Link Features</h3>
                <ul className="list-none space-y-2">
                  <li>🎯 Custom titles and URLs</li>
                  <li>🏷️ Optional badges (NEW, SALE, etc.)</li>
                  <li>📱 Font Awesome icon support</li>
                  <li>🎨 Multiple theme options</li>
                </ul>
              </div>
            </div>
            <div className="card bg-base-200">
              <div className="card-body">
                <h3 className="card-title justify-center mb-4">Perfect For</h3>
                <ul className="list-none space-y-2">
                  <li>🎮 Game promotions</li>
                  <li>🛍️ Merchandise links</li>
                  <li>🔗 Social media profiles</li>
                  <li>📢 Important announcements</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-base-100 py-20">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to customize your stream?</h2>
          <p className="mb-8">Join other streamers using XStreamLink to showcase their content and merchandise.</p>
          <button 
            className="btn btn-primary btn-lg"
            onClick={handleTwitchConnect}
            disabled={!config}
          >
            {config ? 'Start Customizing' : 'Loading...'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage; 